import { NextSeo } from "next-seo";
import SimpleFooter from "components/SimpleFooter";
import SimpleNavbar from "components/SimpleNavbar";
import Link from "next/link";

const PrivacyPolicy = () => {
  return (
    <>
      <NextSeo title="Teamshares | Privacy Policy" />
      <div className="min-h-screen antialiased leading-normal text-gray-900 bg-white">
        <SimpleNavbar />
        <div className="max-w-6xl px-12 mx-auto text-sm lg:text-lg">
          <div className="mb-10 text-3xl font-bold pt-28 md:text-4xl">
            Teamshares Inc. California Personnel Privacy Notice
          </div>
          <div className="mb-2 font-bold lg:mb-4">
            Last Updated: <span className="font-normal"> 08/14/2023</span>
          </div>
          Teamshares Inc. and its affiliates (collectively, &ldquo;
          <span className="font-bold">Teamshares</span>,&rdquo; &ldquo;
          <span className="font-bold">we</span>,&rdquo; &ldquo;
          <span className="font-bold">us</span>,&rdquo; or &ldquo;
          <span className="font-bold">our</span>&rdquo;) respect your privacy.
          This California Personnel Privacy Notice (&ldquo;
          <span className="font-bold">Notice</span>&rdquo;) applies to Personal
          Information that we collect as a Business from or about our current
          and former employees, applicants, contractors, consultants,
          stockholders, directors, and officers who are California residents
          (collectively, &ldquo;<span className="font-bold">you,</span>&rdquo;
          or &ldquo;<span className="font-bold">personnel</span>&rdquo;) in the
          context of their employment or other similar relationship with us.
          <div className="mt-2 lg:mt-4"></div>
          For the purposes of this Notice, &ldquo;
          <span className="font-bold">Personal Information</span>&rdquo; and
          other capitalized terms not otherwise defined in this Notice have the
          meanings provided in the California Consumer Privacy Act of 2018, as
          amended by the California Privacy Rights Act of 2020 (California Civil
          Code &sect;&sect; 1798.100 to 1798.199) and its implementing
          regulations, as amended or superseded from time to time (&ldquo;
          <span className="font-bold">CCPA</span>&rdquo;).
          <div className="mt-2 lg:mt-4"></div>
          Personal Information does not include information that is:
          <div className="mt-2 lg:mt-4"></div>
          <ul className="list-disc">
            <li>Publicly available, as defined by the CCPA.</li>
            <li>Deidentified or aggregated.</li>
            <li>Otherwise excluded from the scope of the CCPA.</li>
          </ul>
          <div className="mt-2 lg:mt-4"></div>
          <div className="mt-2 font-bold underline lg:mt-4">
            Collection of Personal Information
          </div>
          <div className="mt-2 lg:mt-4"></div>
          In the preceding twelve months since this Notice was last updated, we
          have collected Personal Information from the following categories of
          sources:
          <div className="mt-2 lg:mt-4"></div>
          <ul className="list-disc">
            <li>You</li>
            <li>Your devices directly</li>
            <li>Affiliates</li>
            <li>Publicly available databases</li>
          </ul>
          <div className="mt-2 lg:mt-4"></div>
          <div className="mt-2 font-bold underline lg:mt-4">
            Collection and Disclosure of Personal Information
          </div>
          <div className="mt-2 lg:mt-4"></div>
          The chart below provides the categories of Personal Information and
          Sensitive Personal Information (as defined by the CCPA) we have
          collected, or disclosed for a business purpose, or sold or “shared”
          (for cross-context behavioral advertising purposes) in the preceding
          twelve months since this Notice was last updated. The examples of
          Personal Information and Sensitive Personal Information provided for
          each category reflect each category’s statutory definition and may not
          reflect all of the specific types of Personal Information and
          Sensitive Personal Information associated with each category.
          <div className="mt-2 lg:mt-4"></div>
          <table className="table-auto border-collapse text-table">
            <thead>
              <tr>
                <th className="border">Category</th>
                <th className="border">We Collected</th>
                <th className="border">We Disclosed</th>
                <th className="border">We Sold/Shared</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <strong>A. Identifiers </strong> <em>Examples: </em>Name,
                  postal address, unique personal identifier, online identifier,
                  internet protocol address, email address, or other similar
                  identifiers.
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    B. Categories of Personal Information in Cal. Civ. Code
                    Section 1798.80(e)
                  </strong>{" "}
                  <em>Examples: </em>Name, signature, social security number,
                  address, telephone number, education, employment, employment
                  history.
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    C. Characteristics of Protected Classifications under
                    California or Federal Law&nbsp;
                  </strong>{" "}
                  <em>Examples: </em>Race or color, ancestry or national origin,
                  religion or creed, age (over 40), sex, gender identity or
                  expression,marital status, military and veteran status.
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>D. Commercial Information</strong> <em>Examples: </em>
                  Records of personal property, products or services purchased,
                  obtained, or considered, or other purchasing or consuming
                  histories or tendencies.
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>E. Biometric Information </strong>
                  <strong>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</strong>
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    F. Internet or Other Electronic Network Activity
                    Information&nbsp;
                  </strong>{" "}
                  <em>Examples: </em>Information regarding your interaction with
                  our networks or internal software applications.&nbsp;
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>G. Geolocation Data</strong>{" "}
                  <strong>
                    <em>Example: </em>
                  </strong>
                  <strong>General physical location.&nbsp;</strong>
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>H. Sensory Information</strong> <em>Examples: </em>
                  Audio, electronic, visual, thermal, olfactory, or similar
                  information.
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    I. Professional or Employment-Related Information
                  </strong>{" "}
                  <em>Examples: </em>Job application or resume information, past
                  and current job history, and job performance information.
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    J. Non-Public Education Information (as defined in 20 U.S.C.
                    1232g; 34 C.F.R. Part 99)
                  </strong>{" "}
                  <em>Examples: </em>Records that are directly related to a
                  student maintained by an educational agency or institution or
                  by a party acting for the agency or institution.&nbsp;&nbsp;
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>K. Inferences Drawn from Personal Information</strong>{" "}
                  <em>Examples:</em> Consumer profiles reflecting a
                  consumer&rsquo;s preferences, characteristics, psychological
                  trends, predispositions, behavior, attitudes, intelligence,
                  abilities, and aptitudes.
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    L. Sensitive Personal Information (Identifiers)
                  </strong>{" "}
                  <em>Examples:</em> A consumer&rsquo;s social security,
                  driver&rsquo;s license, state identification card, or passport
                  number.
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    M. Sensitive Personal Information (Log-In and Financial
                    Information)
                  </strong>{" "}
                  <em>Examples: </em>Account log-in
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    N. Sensitive Personal Information (Precise Geolocation)
                  </strong>
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    O. Sensitive Personal Information (Group Membership)
                  </strong>{" "}
                  <em>Examples: </em>Racial or ethnic origin.
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    P. Sensitive Personal Information (Contents of
                    Communications)
                  </strong>{" "}
                  <em>Examples: </em>The contents of a consumer&rsquo;s email.
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    Q. Sensitive Personal Information (Genetic Data)
                  </strong>
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    R. Sensitive Personal Information (Identifying Biometric
                    Information)
                  </strong>
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    S. Sensitive Personal Information (Personal Information
                    Collected and Analyzed Concerning a Consumer&rsquo;s Health)
                  </strong>
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    T. Sensitive Personal Information (Personal Information
                    Collected and Analyzed Concerning a Consumer&rsquo;s Sex
                    Life or Sexual Orientation)
                  </strong>
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
            </tbody>
          </table>
          <div className="mt-2 lg:mt-4"></div>
          <div className="mt-2 font-bold underline lg:mt-4">
            Disclosure of Personal Information
          </div>
          <div className="mt-2 lg:mt-4"></div>
          In the preceding twelve months since this Notice was last updated, we
          have disclosed Personal Information to the following categories of
          third parties:
          <div className="mt-2 lg:mt-4"></div>
          <ul className="list-disc">
            <li>
              <span className="block">
                <strong>Vendors</strong>: Vendors and service providers.
              </span>
              Personal Information we disclosed: Identifiers, Categories of
              Personal Information in Cal. Civ. Code Section 1798.80(e),
              Characteristics of Protected Classifications under California or
              Federal Law, Internet or Other Electronic Network Activity
              Information, Geolocation Data, Sensory Information, Professional
              or Employment-Related Information, Inferences Drawn from Personal
              Information, Sensitive Personal Information (Identifiers),
              Sensitive Personal Information (Log-In and Financial Information),
              Sensitive Personal Information (Contents of Communications),
              Sensitive Personal Information (Personal Information Collected and
              Analyzed Concerning a Consumer&apos;s Health), and Sensitive
              Personal Information (Personal Information Collected and Analyzed
              Concerning a Consumer&apos;s Sex Life or Sexual Orientation)
            </li>
            <li>
              <span className="block">
                <strong>Affiliates</strong>: Our affiliate companies.
              </span>
              Personal Information we disclosed: Identifiers, Categories of
              Personal Information in Cal. Civ. Code Section 1798.80(e),
              Characteristics of Protected Classifications under California or
              Federal Law, Internet or Other Electronic Network Activity
              Information, Geolocation Data, Sensory Information, Professional
              or Employment-Related Information, Inferences Drawn from Personal
              Information, Sensitive Personal Information (Identifiers),
              Sensitive Personal Information (Log-In and Financial Information),
              Sensitive Personal Information (Group Membership), Sensitive
              Personal Information (Contents of Communications), Sensitive
              Personal Information (Personal Information Collected and Analyzed
              Concerning a Consumer&apos;s Health), and Sensitive Personal
              Information (Personal Information Collected and Analyzed
              Concerning a Consumer&apos;s Sex Life or Sexual Orientation)
            </li>
            <li>
              <span className="block">
                <strong>Third Parties as Legally Required</strong>: Third
                parties as required by law and similar disclosures.
              </span>
              Personal Information we disclosed: Identifiers, Categories of
              Personal Information in Cal. Civ. Code Section 1798.80(e),
              Characteristics of Protected Classifications under California or
              Federal Law, Internet or Other Electronic Network Activity
              Information, Geolocation Data, Sensory Information, Professional
              or Employment-Related Information, Inferences Drawn from Personal
              Information, Sensitive Personal Information (Identifiers),
              Sensitive Personal Information (Log-In and Financial Information),
              Sensitive Personal Information (Contents of Communications),
              Sensitive Personal Information (Personal Information Collected and
              Analyzed Concerning a Consumer&apos;s Health), and Sensitive
              Personal Information (Personal Information Collected and Analyzed
              Concerning a Consumer&apos;s Sex Life or Sexual Orientation)
            </li>
            <li>
              <span className="block">
                <strong>Third Parties with Consent</strong>: Other third parties
                for whom we have obtained your permission to disclose your
                Personal Information.
              </span>
              Personal Information we disclosed: Identifiers, Categories of
              Personal Information in Cal. Civ. Code Section 1798.80(e),
              Characteristics of Protected Classifications under California or
              Federal Law, Internet or Other Electronic Network Activity
              Information, Geolocation Data, Sensory Information, Professional
              or Employment-Related Information, Inferences Drawn from Personal
              Information, Sensitive Personal Information (Identifiers),
              Sensitive Personal Information (Log-In and Financial Information),
              Sensitive Personal Information (Contents of Communications),
              Sensitive Personal Information (Personal Information Collected and
              Analyzed Concerning a Consumer&apos;s Health), and Sensitive
              Personal Information (Personal Information Collected and Analyzed
              Concerning a Consumer&apos;s Sex Life or Sexual Orientation)
            </li>
          </ul>
          <div className="mt-2 lg:mt-4"></div>
          <div className="mt-2 font-bold underline lg:mt-4">
            Use of Personal Information
          </div>
          <div className="mt-2 lg:mt-4"></div>
          We collect, use, or disclose your Personal Information in accordance
          with the specific business purposes below:
          <div className="mt-2 lg:mt-4"></div>
          <ul className="list-disc">
            <li>
              <strong>Recruitment</strong>: To receive, evaluate, and process
              job applications, verify your information and conduct background
              checks, as permitted by law, communicate with you about the
              recruitment process and your application, schedule interviews, and
              improve the recruitment process.
            </li>
            <li>
              <strong>HR Operations</strong>: To fulfill your employment
              agreement, manage employee onboarding, promotions, transfers, and
              secondments, manage HR related claims and litigation, provide
              trainings, performance reviews, and evaluations, manage business
              and travel expenses, and create reports for workforce planning,
              such as headcount and employee turnover.
            </li>
            <li>
              <strong>Payroll and Benefits</strong>: To administer pay and
              benefits, process business and travel expense reimbursements,
              calculate tax and social security withholdings, comply with wage
              garnishment orders, and process leaves.
            </li>
            <li>
              <strong>IT and Security</strong>: To provide IT and security
              support, manage IT resources and provisions of new IT
              applications, manage log-in credentials, monitor IT systems and
              networks for suspicious activity, detect intrusions, and monitor
              and control access to facilities to protect us, our personnel, and
              property.
            </li>
            <li>
              <strong>Legal and Compliance</strong>: To comply with legal
              obligations regarding HR analytics, including requests from
              government statistics services, process conflict of interest
              reporting, process employee work-related claims such as worker
              compensation or insurance claims ensure compliance with our
              employee policies and security requirements gather evidence for
              and to support any internal investigations, litigation,
              disciplinary action, termination, or related activities, protect,
              enforce, and defend the legal rights, privacy, safety, or property
              of us and our employees, agents, and contractors, protect against
              fraud and manage risk, and comply with applicable laws,
              regulations, legal processes, or enforceable governmental
              requests.
            </li>
            <li>
              <strong>Acquisition or Merger</strong>: To manage acquisitions,
              mergers, and reorganizations or sale of some or all of a company.
            </li>
            <li>
              <strong>Other Business Purposes</strong>: For other purposes that
              you would reasonably expect, or for which we provide specific
              Notice at the time the information is collected.
            </li>
          </ul>
          <div className="mt-2 lg:mt-4"></div>
          <div className="mt-2 font-bold underline lg:mt-4">Data Retention</div>
          <div className="mt-2 lg:mt-4"></div>
          We retain Personal Information for as long as needed or permitted in
          light of the purpose(s) for which it was obtained and consistent with
          applicable law. The criteria used to determine our retention periods
          for each category of Personal Information include:
          <div className="mt-2 lg:mt-4"></div>
          <ul className="list-disc">
            <li>
              The length of time we have an ongoing relationship with you and
              the length of time thereafter during which we may have a
              legitimate need to reference your Personal Information to address
              issues that may arise;
            </li>
            <li>
              Whether there is a legal obligation to which we are subject that
              requires us to retain information for, or delete information
              after, a certain period of time; and
            </li>
            <li>
              The length of time we use the information to detect and prevent
              fraud and security incidents.
            </li>
          </ul>
          <div className="mt-2 lg:mt-4"></div>
          <div className="mt-2 font-bold underline lg:mt-4">
            Your California Privacy Rights
          </div>
          <div className="mt-2 lg:mt-4"></div>
          If you are a California resident, you may exercise the following
          rights.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Right to Know and Access.</span>
          You may submit a verifiable request for information regarding the: (1)
          categories of Personal Information collected or disclosed by us; (2)
          purposes for which categories of Personal Information are collected by
          us; (3) categories of sources from which we collect Personal
          Information; (4) categories of third parties to whom we disclosed
          Personal Information; and (5) specific pieces of Personal Information
          we have collected about you during the past twelve months.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Right to Delete.</span>
          Subject to certain exceptions, you may submit a verifiable request
          that we delete Personal Information about you that we have collected
          from you.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Right to Correct.</span>
          You have the right to correct inaccurate Personal Information that we
          maintain about you.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Verification.</span>
          Requests for access to, deletion, or correction of Personal
          Information are subject to our ability to reasonably verify your
          identity in light of the information requested and pursuant to
          relevant CCPA requirements, limitations, and regulations. To verify
          your access, deletion, or correction request, please follow the
          instructions provided in our response to your request.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Right to Opt Out.</span>
          In some circumstances, you may opt out of the sale or sharing (for
          cross-context behavioral advertising purposes) of your Personal
          Information. We do not sell or share (for cross-context behavioral
          advertising purposes) your Personal Information.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">
            Right to Limit Use and Disclosure of Sensitive Personal Information.
          </span>
          In some circumstances, you may limit our use and disclosure of your
          Sensitive Personal Information. We do not use or disclose your
          Sensitive Personal Information for purposes other than the limited
          purposes permitted by the CCPA.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Right of No Retaliation.</span>
          You have the right not to receive discriminatory treatment for the
          exercise of your CCPA privacy rights, subject to certain limitations,
          including if you are an employee, applicant, or independent contractor
          of our business.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Submit Requests.</span>
          To exercise your rights under the CCPA, send an email via our Contact
          Information section.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Authorizing an Agent.</span>
          If you are acting as an authorized agent to make a request to know,
          delete, correct, or opt out on behalf of a California resident, send
          an email via our Contact Information section below and attach a
          written authorization signed by the resident.
          <div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">Contact Information.</span>
          <div className="mt-2 lg:mt-4"></div>
          If you have any questions, comments, or concerns about this notice or
          our processing activities, please email us at{" "}
          <Link
            href="mailto:support@teamshares.com"
            target="_blank"
            rel="noopener"
          >
            support@teamshares.com
          </Link>{" "}
          or write to us at:
          <div className="mt-2 lg:mt-4"></div>
          Teamshares Inc.
          <br />
          214 Sullivan Street, Suite 6B
          <br />
          NY, NY 10012
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">
            We will make an effort to reply within a reasonably timeframe.
            Please feel free to reach out to us at any time.
          </span>
        </div>
        <SimpleFooter />
      </div>
    </>
  );
};

export default PrivacyPolicy;
